// Chain-Timestamp-Enrichment.
// ─────────────────────────────────────────────────────────────────────────────
// Fuer jede TX mit Blockchain-Hash, die noch keine Sekundenpraezision hat,
// holen wir die exakte Block-Zeit ueber /api/blockchain-lookup (Blockchair +
// GoldRush Fallback). Damit landen Wallet-/On-Chain-Exporte ohne Uhrzeit
// (Ledger Live, Phantom, alte MetaMask-Exporte) zuverlaessig in der richtigen
// FIFO-Reihenfolge — ohne dass der User irgendwas einstellen muss.
//
// Wird zweimal getriggert:
//   1) Nach jedem CSV/Dokument-Import — silent, bevor Firestore-Write.
//   2) Einmal pro Browser-Session als Backfill fuer bestehende TXs ohne Zeit.

const API_BASE = process.env.REACT_APP_API_BASE || '';

// Coins, fuer die /api/blockchain-lookup ein Ergebnis liefern kann.
// (Synchron gehalten mit BLOCKCHAIR_CHAIN + GOLDRUSH_CHAIN in api/blockchain-lookup.js.)
const SUPPORTED_COINS = new Set([
  'BTC','ETH','BNB','XRP','XLM','ADA','SOL','SUI','AVAX','MATIC','POL',
  'TRX','LTC','DOGE','DOT','XMR','ETC','BCH','XTZ','ALGO','ATOM','NEAR',
  'FLR','HBAR','FTM','ARB','OP','BASE',
]);

const UNRESOLVABLE_KEY = 'red_chain_ts_unresolvable_v1';

function loadUnresolvableSet() {
  try {
    const raw = localStorage.getItem(UNRESOLVABLE_KEY);
    if (!raw) return new Set();
    const arr = JSON.parse(raw);
    return new Set(Array.isArray(arr) ? arr : []);
  } catch {
    return new Set();
  }
}

function persistUnresolvableSet(set) {
  try {
    // Begrenzen, damit der localStorage-Eintrag nicht unbegrenzt waechst.
    const arr = [...set].slice(-2000);
    localStorage.setItem(UNRESOLVABLE_KEY, JSON.stringify(arr));
  } catch {
    /* localStorage voll oder gesperrt – kein Drama */
  }
}

function coinForLookup(tx) {
  return String(tx.coin || tx.coinOut || tx.coinIn || '').toUpperCase();
}

function needsEnrichment(tx, unresolvable) {
  if (!tx || !tx.hash) return false;
  if (tx.tsPrecision === 'second') return false;
  const coin = coinForLookup(tx);
  if (!SUPPORTED_COINS.has(coin)) return false;
  if (unresolvable.has(`${coin}|${tx.hash}`)) return false;
  return true;
}

async function lookupOne(coin, hash, signal) {
  const url = `${API_BASE}/api/blockchain-lookup?coin=${encodeURIComponent(coin)}&hash=${encodeURIComponent(hash)}`;
  try {
    const res = await fetch(url, { signal });
    if (!res.ok) return null;
    const data = await res.json();
    return data?.timestamp || null;
  } catch {
    return null;
  }
}

function buildPatch(isoTs) {
  if (!isoTs) return null;
  const d = new Date(isoTs);
  if (Number.isNaN(d.getTime())) return null;
  const iso = d.toISOString();
  return {
    datum:       iso.slice(0, 10),
    zeit:        iso.slice(11, 19),
    ts:          iso,
    tsPrecision: 'second',
    tsSource:    'blockchain',
  };
}

// Kleiner Concurrency-Limiter — verhindert Burst auf Blockchair und CPU-Spikes
// im Browser. concurrency=4 ist konservativ und reicht: typische Backfills sind
// < 100 TXs und in Sekunden durch.
async function runWithConcurrency(items, worker, concurrency = 4) {
  const results = new Array(items.length);
  let cursor = 0;
  const lane = async () => {
    while (cursor < items.length) {
      const idx = cursor++;
      results[idx] = await worker(items[idx], idx);
    }
  };
  const lanes = Array.from({ length: Math.min(concurrency, items.length) }, lane);
  await Promise.all(lanes);
  return results;
}

/**
 * Reichert ein TX-Array um Block-Zeitstempel an (immutable Copy).
 *
 * @param {Array} txs
 * @param {{ concurrency?: number, signal?: AbortSignal }} [opts]
 * @returns {Promise<{ txs: Array, enrichedCount: number }>}
 */
export async function enrichTimestamps(txs, opts = {}) {
  if (!Array.isArray(txs) || txs.length === 0) return { txs, enrichedCount: 0 };
  const unresolvable = loadUnresolvableSet();
  const targets = [];
  for (let i = 0; i < txs.length; i++) {
    if (needsEnrichment(txs[i], unresolvable)) targets.push(i);
  }
  if (targets.length === 0) return { txs, enrichedCount: 0 };

  const out = txs.slice();
  let enrichedCount = 0;

  await runWithConcurrency(targets, async (i) => {
    const tx = txs[i];
    const coin = coinForLookup(tx);
    const ts = await lookupOne(coin, tx.hash, opts.signal);
    const patch = buildPatch(ts);
    if (patch) {
      out[i] = { ...tx, ...patch };
      enrichedCount++;
    } else {
      unresolvable.add(`${coin}|${tx.hash}`);
    }
  }, opts.concurrency || 4);

  persistUnresolvableSet(unresolvable);
  return { txs: out, enrichedCount };
}

/**
 * Backfill-Variante: ruft pro angereicherter TX einen Patch-Callback auf
 * (statt ein neues Array zurueckzugeben). Genau das, was Firestore-Writes
 * brauchen — der Caller kontrolliert Batches und Reauth.
 *
 * @param {Array} txs                   Mit _docId
 * @param {(docId: string, patch: object) => Promise<void>} onPatch
 * @param {{ concurrency?: number, signal?: AbortSignal }} [opts]
 * @returns {Promise<{ enrichedCount: number, attemptedCount: number }>}
 */
export async function enrichTimestampsAndPatch(txs, onPatch, opts = {}) {
  if (!Array.isArray(txs) || txs.length === 0 || typeof onPatch !== 'function') {
    return { enrichedCount: 0, attemptedCount: 0 };
  }
  const unresolvable = loadUnresolvableSet();
  const targets = txs.filter(tx => tx._docId && needsEnrichment(tx, unresolvable));
  if (targets.length === 0) return { enrichedCount: 0, attemptedCount: 0 };

  let enrichedCount = 0;

  await runWithConcurrency(targets, async (tx) => {
    const coin = coinForLookup(tx);
    const ts = await lookupOne(coin, tx.hash, opts.signal);
    const patch = buildPatch(ts);
    if (patch) {
      try {
        await onPatch(tx._docId, patch);
        enrichedCount++;
      } catch {
        /* Patch-Fehler ignorieren, naechster Versuch ist die naechste Session */
      }
    } else {
      unresolvable.add(`${coin}|${tx.hash}`);
    }
  }, opts.concurrency || 4);

  persistUnresolvableSet(unresolvable);
  return { enrichedCount, attemptedCount: targets.length };
}
